import { useEffect, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DollarSign, AlertTriangle, CheckCircle2, Clock, Send, Ban, PlayCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentManagement({ user }) {
  const [transactions, setTransactions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [sendingLink, setSendingLink] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [txnRes, orgRes] = await Promise.all([
        api.get('/admin/payment-transactions'),
        api.get('/admin/organizations'),
      ]);
      setTransactions(txnRes.data.transactions || []);
      setOrganizations(orgRes.data || []);
    } catch {
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const runPaymentCheck = async () => {
    try {
      await api.get('/admin/payment-check');
      toast.success('Payment check completed');
      loadData();
    } catch {
      toast.error('Failed to run payment check');
    }
  };

  const suspendOrg = async (orgId) => {
    if (!window.confirm('Suspend this franchise? They will lose access immediately.')) return;
    try {
      await api.put(`/admin/organizations/${orgId}`, { status: 'suspended' });
      toast.success('Franchise suspended');
      loadData();
    } catch {
      toast.error('Failed to suspend');
    }
  };

  const reactivateOrg = async (orgId) => {
    try {
      await api.put(`/admin/organizations/${orgId}`, { status: 'active', next_payment_date: new Date(Date.now() + 30 * 86400000).toISOString(), payment_reminder_sent: false });
      toast.success('Franchise reactivated with 30-day billing cycle');
      loadData();
    } catch {
      toast.error('Failed to reactivate');
    }
  };

  const sendPaymentLink = async (orgId) => {
    setSendingLink(orgId);
    try {
      const res = await api.post('/payments/monthly-checkout', {
        organization_id: orgId,
        origin_url: window.location.origin,
      });
      // Copy the payment URL to clipboard
      try {
        await navigator.clipboard.writeText(res.data.url);
        toast.success('Payment link copied to clipboard! Send it to the franchise owner.');
      } catch {
        // Fallback: show the URL in the toast
        toast.success(`Payment link generated! URL: ${res.data.url}`, { duration: 10000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate payment link');
    } finally {
      setSendingLink(null);
    }
  };

  if (user.role !== 'super_admin') {
    return <div className="text-center py-12 text-slate-500">Super Admin access required.</div>;
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  // Stats
  const totalRevenue = transactions.filter(t => t.payment_status === 'paid').reduce((sum, t) => sum + (t.amount || 0), 0);
  const activeOrgs = organizations.filter(o => o.status === 'active' && o.setup_fee_paid);
  const suspendedOrgs = organizations.filter(o => o.status === 'suspended');
  const now = new Date();
  const overdueOrgs = organizations.filter(o => {
    if (o.status !== 'active' || !o.next_payment_date) return false;
    return new Date(o.next_payment_date) < now;
  });

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'franchises', label: 'Franchises' },
    { key: 'transactions', label: 'Transactions' },
  ];

  return (
    <div className="space-y-6" data-testid="payment-management-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Payment Management</h1>
          <p className="text-slate-600 mt-1">Track payments, send billing links, and manage franchise status</p>
        </div>
        <Button variant="outline" size="sm" onClick={runPaymentCheck} data-testid="run-payment-check-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Run Payment Check
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="stat-revenue">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100"><DollarSign className="w-5 h-5 text-green-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900">${totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-active">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100"><CheckCircle2 className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Active Franchises</p>
                <p className="text-2xl font-bold text-slate-900">{activeOrgs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-suspended">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100"><Ban className="w-5 h-5 text-red-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Suspended</p>
                <p className="text-2xl font-bold text-slate-900">{suspendedOrgs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-overdue">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Overdue</p>
                <p className="text-2xl font-bold text-slate-900">{overdueOrgs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {TABS.map(t => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'overview' && (
        <div className="space-y-4">
          {overdueOrgs.length > 0 && (
            <Card className="border-amber-300 bg-amber-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-4 h-4" /> Overdue Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overdueOrgs.map(o => (
                    <div key={o.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-200">
                      <div>
                        <p className="font-medium text-slate-900">{o.company_name}</p>
                        <p className="text-xs text-slate-500">Code: {o.master_code} &middot; Due: {new Date(o.next_payment_date).toLocaleDateString()} &middot; ${o.monthly_price}/mo</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => sendPaymentLink(o.id)} disabled={sendingLink === o.id} data-testid={`send-link-${o.id}`}>
                          <Send className="w-3 h-3 mr-1" /> {sendingLink === o.id ? 'Generating...' : 'Copy Pay Link'}
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => suspendOrg(o.id)} data-testid={`suspend-${o.id}`}>
                          <Ban className="w-3 h-3 mr-1" /> Suspend
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {suspendedOrgs.length > 0 && (
            <Card className="border-red-300 bg-red-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-red-800">
                  <Ban className="w-4 h-4" /> Suspended Franchises
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {suspendedOrgs.map(o => (
                    <div key={o.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-200">
                      <div>
                        <p className="font-medium text-slate-900">{o.company_name}</p>
                        <p className="text-xs text-slate-500">Code: {o.master_code} &middot; {o.plan?.toUpperCase()} &middot; ${o.monthly_price}/mo</p>
                      </div>
                      <Button size="sm" variant="outline" className="text-green-600" onClick={() => reactivateOrg(o.id)} data-testid={`reactivate-${o.id}`}>
                        <PlayCircle className="w-3 h-3 mr-1" /> Reactivate
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Transactions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">No transactions yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="recent-transactions-table">
                    <thead>
                      <tr className="border-b text-left text-slate-500">
                        <th className="pb-2 font-medium">Company</th>
                        <th className="pb-2 font-medium">Type</th>
                        <th className="pb-2 font-medium">Amount</th>
                        <th className="pb-2 font-medium">Status</th>
                        <th className="pb-2 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 10).map((t, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2.5 font-medium text-slate-900">{t.company_name}</td>
                          <td className="py-2.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.type === 'franchise_signup' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                              {t.type === 'franchise_signup' ? 'Signup' : 'Monthly'}
                            </span>
                          </td>
                          <td className="py-2.5 font-medium">${t.amount?.toFixed(2)}</td>
                          <td className="py-2.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              t.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                              t.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {t.payment_status}
                            </span>
                          </td>
                          <td className="py-2.5 text-slate-500">{new Date(t.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'franchises' && (
        <div className="space-y-3">
          {organizations.filter(o => o.setup_fee_paid || o.next_payment_date).length === 0 ? (
            <Card><CardContent className="py-8 text-center text-slate-500">No paid franchises yet</CardContent></Card>
          ) : (
            organizations.filter(o => o.setup_fee_paid || o.next_payment_date).map(o => {
              const isPastDue = o.next_payment_date && new Date(o.next_payment_date) < now;
              return (
                <Card key={o.id} className={`${o.status === 'suspended' ? 'border-red-200 bg-red-50/30' : isPastDue ? 'border-amber-200 bg-amber-50/30' : ''}`} data-testid={`franchise-${o.id}`}>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-slate-900">{o.company_name}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${o.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {o.status}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 uppercase">{o.plan}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>Code: <b className="text-slate-700">{o.master_code}</b></span>
                          <span>${o.monthly_price}/mo</span>
                          {o.next_payment_date && (
                            <span className={isPastDue ? 'text-red-600 font-medium' : ''}>
                              <Clock className="w-3 h-3 inline mr-0.5" />
                              {isPastDue ? 'OVERDUE' : 'Next'}: {new Date(o.next_payment_date).toLocaleDateString()}
                            </span>
                          )}
                          <span>{o.admin_email}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button size="sm" variant="outline" onClick={() => sendPaymentLink(o.id)} disabled={sendingLink === o.id} data-testid={`pay-link-${o.id}`}>
                          <Send className="w-3 h-3 mr-1" /> {sendingLink === o.id ? '...' : 'Pay Link'}
                        </Button>
                        {o.status === 'active' ? (
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => suspendOrg(o.id)}>
                            <Ban className="w-3 h-3 mr-1" /> Suspend
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="text-green-600" onClick={() => reactivateOrg(o.id)}>
                            <PlayCircle className="w-3 h-3 mr-1" /> Reactivate
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {tab === 'transactions' && (
        <Card>
          <CardContent className="pt-5">
            {transactions.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">No transactions yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="all-transactions-table">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="pb-2 pr-4 font-medium">Company</th>
                      <th className="pb-2 pr-4 font-medium">Plan</th>
                      <th className="pb-2 pr-4 font-medium">Type</th>
                      <th className="pb-2 pr-4 font-medium">Amount</th>
                      <th className="pb-2 pr-4 font-medium">Status</th>
                      <th className="pb-2 pr-4 font-medium">Email</th>
                      <th className="pb-2 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 font-medium text-slate-900">{t.company_name}</td>
                        <td className="py-2.5 pr-4 uppercase text-xs">{t.plan}</td>
                        <td className="py-2.5 pr-4">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.type === 'franchise_signup' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                            {t.type === 'franchise_signup' ? 'Signup' : 'Monthly'}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 font-medium">${t.amount?.toFixed(2)}</td>
                        <td className="py-2.5 pr-4">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            t.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                            t.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {t.payment_status}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-slate-500">{t.email}</td>
                        <td className="py-2.5 text-slate-500">{new Date(t.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
